const AIRTABLE_BASE_ID = import.meta.env.VITE_AIRTABLE_BASE_ID;
const AIRTABLE_PAT = import.meta.env.VITE_AIRTABLE_PAT;
const TABLE_NAME = import.meta.env.VITE_AIRTABLE_TABLE_NAME || 'Inscribed Students';
const TABLE_PARTICIPANTS = import.meta.env.VITE_AIRTABLE_TABLE_PARTICIPANTS_DATA || 'Participants Data';

const AIRTABLE_URL = `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${encodeURIComponent(TABLE_NAME)}`;
const PARTICIPANTS_URL = `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${encodeURIComponent(TABLE_PARTICIPANTS)}`;

export interface CognitiveRecord {
  account: string;
  name: string;
  role: string;
  memoryScore: number;
  attentionScore: number;
  reasoningScore: number;
  baseScore: number;
  resultCard: string;
  submittedAt: string;
  rawAnswers: string;
}

export async function saveCognitiveRecord(record: CognitiveRecord) {
  const response = await fetch(AIRTABLE_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${AIRTABLE_PAT}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      records: [
        {
          fields: {
            Account: record.account,
            Name: record.name,
            Role: record.role,
            'Memory Score': record.memoryScore,
            'Attention Score': record.attentionScore,
            'Reasoning Score': record.reasoningScore,
            'Base Score': record.baseScore,
            'Result Card': record.resultCard,
            'Submitted At': record.submittedAt,
            'Raw Answers': record.rawAnswers
          }
        }
      ]
    })
  });

  if (!response.ok) {
    const error = await response.json();
    console.error('Airtable error:', error);
    throw new Error(error?.error?.message || 'Failed to save to Airtable');
  }

  return response.json();
}

// ─── Participants Data (Session Collector) ────────────────────────────────────

export interface ParticipantSession {
  id?: string;
  teamName: string;
  prompt: string;
  aiScore?: number;
}

export async function saveParticipantSession(record: ParticipantSession) {
  const response = await fetch(PARTICIPANTS_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${AIRTABLE_PAT}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      records: [
        {
          fields: {
            TeamName: record.teamName,
            Prompt: record.prompt
          }
        }
      ]
    })
  });

  if (!response.ok) {
    const error = await response.json();
    console.error('Airtable error:', error);
    throw new Error(error?.error?.message || 'Failed to save participant session');
  }

  return response.json();
}

export async function getParticipantSessions(): Promise<ParticipantSession[]> {
  const response = await fetch(PARTICIPANTS_URL, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${AIRTABLE_PAT}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    console.error('Airtable error:', error);
    throw new Error(error?.error?.message || 'Failed to fetch participant sessions');
  }

  const data = await response.json();
  return data.records.map((r: { id: string; fields: Record<string, unknown> }) => ({
    id: r.id,
    teamName: r.fields['TeamName'] || '',
    prompt: r.fields['Prompt'] || '',
    aiScore: r.fields['AI Score'] ?? 0
  }));
}

export async function deleteAllParticipantSessions(): Promise<void> {
  // First fetch all record IDs
  const response = await fetch(PARTICIPANTS_URL, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${AIRTABLE_PAT}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch records for deletion');
  }

  const data = await response.json();
  const ids: string[] = data.records.map((r: { id: string }) => r.id);

  if (ids.length === 0) return;

  // Airtable allows max 10 records per DELETE request
  for (let i = 0; i < ids.length; i += 10) {
    const batch = ids.slice(i, i + 10);
    const params = batch.map((id) => `records[]=${id}`).join('&');
    const delResponse = await fetch(`${PARTICIPANTS_URL}?${params}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${AIRTABLE_PAT}`,
        'Content-Type': 'application/json'
      }
    });

    if (!delResponse.ok) {
      const error = await delResponse.json();
      console.error('Airtable delete error:', error);
      throw new Error(error?.error?.message || 'Failed to delete records');
    }
  }
}

export async function updateParticipantScore(recordId: string, aiScore: number): Promise<void> {
  const response = await fetch(PARTICIPANTS_URL, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${AIRTABLE_PAT}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      records: [
        {
          id: recordId,
          fields: {
            'AI Score': aiScore
          }
        }
      ]
    })
  });

  if (!response.ok) {
    const error = await response.json();
    console.error('Airtable update error:', error);
    throw new Error(error?.error?.message || 'Failed to update score');
  }
}

// ─── Cognitive Participants (Inscribed Students) ──────────────────────────────

export interface CognitiveParticipant {
  id: string;
  name: string;
  role: string;
  account: string;
  resultCard: string;
  memoryScore: number;
  attentionScore: number;
  reasoningScore: number;
  baseScore: number;
  promptPiece?: string;
}

export async function getCognitiveParticipants(): Promise<CognitiveParticipant[]> {
  const response = await fetch(AIRTABLE_URL, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${AIRTABLE_PAT}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error?.message || 'Failed to fetch participants');
  }

  const data = await response.json();
  return data.records.map((r: { id: string; fields: Record<string, unknown> }) => ({
    id: r.id,
    name: (r.fields['Name'] as string) || '',
    role: (r.fields['Role'] as string) || '',
    account: (r.fields['Account'] as string) || '',
    resultCard: (r.fields['Result Card'] as string) || '',
    memoryScore: (r.fields['Memory Score'] as number) || 0,
    attentionScore: (r.fields['Attention Score'] as number) || 0,
    reasoningScore: (r.fields['Reasoning Score'] as number) || 0,
    baseScore: (r.fields['Base Score'] as number) || 0,
    promptPiece: (r.fields['Prompt Piece'] as string) || ''
  }));
}

export async function updateParticipantPromptPiece(recordId: string, promptPiece: string): Promise<void> {
  const response = await fetch(AIRTABLE_URL, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${AIRTABLE_PAT}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      records: [
        {
          id: recordId,
          fields: {
            'Prompt Piece': promptPiece
          }
        }
      ]
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error?.error?.message || 'Failed to update prompt piece');
  }
}
